import { db } from "../firebase";
import {
  collection, addDoc, doc, setDoc, updateDoc,
  serverTimestamp, increment,
} from "firebase/firestore";
import {
  getPendingCheckins,
  deletePendingCheckin,
  getPendingNotes,
  deletePendingNote,
  getPendingSales,
  deletePendingSale,
  setMeta,
} from "./offlineDB";

/**
 * Syncs all pending check-ins, notes, and sales to Firestore.
 * Returns { synced, failed } counts.
 * Idempotent — safe to call multiple times.
 */
export async function syncPendingData(companyId) {
  let synced = 0;
  let failed = 0;

  // ── Sync pending check-ins ─────────────────────────────────────────────────
  const checkins = await getPendingCheckins();
  for (const item of checkins) {
    try {
      const { localId, ...checkinData } = item;

      await addDoc(
        collection(db, "companies", companyId, "events"),
        {
          ...checkinData.event,
          timestamp: serverTimestamp(),
          offline: true,
        }
      );

      if (checkinData.clientId && checkinData.newStatus) {
        await updateDoc(
          doc(db, "companies", companyId, "clients", checkinData.clientId),
          {
            last_status:     checkinData.newStatus,
            last_visited_by: checkinData.vendorId || null,
            last_visited_at: serverTimestamp(),
          }
        );
      }

      await deletePendingCheckin(localId);
      synced++;
    } catch {
      failed++;
    }
  }

  // ── Sync pending notes ─────────────────────────────────────────────────────
  const notes = await getPendingNotes();
  for (const item of notes) {
    try {
      const { localId, ...noteData } = item;

      await addDoc(
        collection(db, "companies", companyId, "notes"),
        {
          ...noteData.note,
          timestamp: serverTimestamp(),
          offline: true,
        }
      );

      await deletePendingNote(localId);
      synced++;
    } catch {
      failed++;
    }
  }

  // ── Sync pending sales ─────────────────────────────────────────────────────
  const sales = await getPendingSales();
  for (const sale of sales) {
    try {
      // Deterministic doc ID — safe to re-run (idempotent)
      const saleDocId = `offline_${sale.localId}`;

      // Write sale document
      await setDoc(
        doc(db, "companies", companyId, "sales", saleDocId),
        {
          vendor_id:    sale.vendorId,
          vendor_name:  sale.vendorName,
          client_id:    sale.clientId    || null,
          client_name:  sale.clientName  || "Unknown",
          items:        sale.items,
          total_items:  sale.items.reduce((s, i) => s + i.quantity, 0),
          total_amount: sale.totalAmount,
          created_at:   serverTimestamp(),
          offline:      true,
          offline_time: new Date(sale.savedAt).toISOString(),
        }
      );

      // Deduct stock + write movement records for each item
      for (const item of sale.items) {
        const stockRef = doc(
          db,
          "companies", companyId,
          "vendor_stock", sale.vendorId,
          "items", item.item_id
        );
        try {
          await updateDoc(stockRef, {
            quantity:   increment(-item.quantity),
            updated_at: serverTimestamp(),
          });
        } catch {
          // Stock doc may not exist yet — not fatal
        }

        const movDocId = `offline_${sale.localId}_${item.item_id}`;
        await setDoc(
          doc(db, "companies", companyId, "stock_movements", movDocId),
          {
            type:         "sale",
            vendor_id:    sale.vendorId,
            vendor_name:  sale.vendorName,
            product_id:   item.item_id,
            product_name: item.item_name,
            quantity:     item.quantity,
            sale_id:      saleDocId,
            note:         `Sale to ${sale.clientName || "client"}`,
            created_at:   serverTimestamp(),
            offline:      true,
          }
        );
      }

      await deletePendingSale(sale.localId);
      synced++;
    } catch {
      failed++;
    }
  }

  // ── Record last sync time ──────────────────────────────────────────────────
  if (synced > 0) {
    await setMeta("lastSyncTime", Date.now());
  }

  return { synced, failed };
}
